import React from 'react';
import { RefreshCw, Archive, Tag } from 'lucide-react'; // Added Tag icon
import ListingActionButton from './ListingActionButton';
import PremiumActionButton from './PremiumActionButton';
import { Edit2, Eye, Trash2, ShoppingBag, Award, XCircle } from 'lucide-react';

interface ListingActionsOverlayProps {
  listingId: number;
  isPremium: boolean;
  isSold: boolean;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onToggleSold: (id: number, isSold: boolean) => void;
  onTogglePremium: (id: number, isPremium: boolean) => void;
  onDelete: ((id: number, isArchived?: boolean) => void) | undefined; // Corrected from previous state to allow undefined
  onDeactivate?: (id: number) => void; 
  isActive: boolean;
  onReactivate?: (id: number, isFromArchive?: boolean, originalId?: number) => void;
  isArchived?: boolean;
  stock?: number;
  originalId?: number;
  approvalStatus?: string; // Added approvalStatus prop
}

const ListingActionsOverlay: React.FC<ListingActionsOverlayProps> = ({
  listingId,
  isPremium,
  isSold,
  isActive,
  onView,
  onEdit,
  onToggleSold,
  onTogglePremium,
  onDelete,
  onDeactivate,
  onReactivate,
  isArchived = false,
  stock = 0,
  originalId,
  approvalStatus, // Destructure approvalStatus
}) => {
  const isPending = approvalStatus === 'pending';
  const isRejected = approvalStatus === 'rejected';


  return (
    <div className={`absolute inset-0 ${isActive ? 'bg-black/60' : 'bg-black/80'} opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10`}>
      <div className="grid grid-cols-2 gap-2 p-4">
        {/* Stock indicator - shown for all listings */}
        {stock !== undefined && (
          <div className="col-span-2 mb-1 flex justify-center">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
              <Tag className="h-3 w-3 mr-1" />
              {stock} në stok
            </span>
          </div>
        )}
        
        <ListingActionButton 
          label="Shiko"
          onClick={() => onView(listingId)}
          icon={Eye}
          variant="secondary"
        />
        
        {isActive && !isPending && !isRejected && ( // Disable edit if pending or rejected
          <ListingActionButton 
            label="Ndrysho"
            onClick={() => onEdit(listingId)}
            icon={Edit2}
            variant="secondary"
          />
        )}
        
        {isActive && !isPending && !isRejected && ( // Disable toggle sold if pending or rejected
          <ListingActionButton 
            label={isSold ? "Shëno si e disponueshme" : "Shëno si e shitur"}
            onClick={() => onToggleSold(listingId, isSold)}
            icon={ShoppingBag}
            variant={isSold ? "outline" : "default"}
          />
        )}
        
        {!isPremium && isActive && !isPending && !isRejected && ( // Disable premium if pending or rejected
          <PremiumActionButton 
            isPremium={isPremium}
            onTogglePremium={() => onTogglePremium(listingId, isPremium)}
            icon={<Award size={16} />}
          />
        )}
        
        {isActive && onDeactivate && !isPending && !isRejected && ( // Disable deactivate if pending or rejected
          <ListingActionButton 
            label="Çaktivizo"
            onClick={() => onDeactivate(listingId)}
            icon={Archive}
            variant="outline"
            className="border-orange-500 text-orange-500 hover:bg-orange-50"
          />
        )}
        
        {!isActive && onReactivate && !isRejected && ( // Allow reactivate even if pending (e.g., was pending, then deactivated, then reactivated still as pending)
                                                        // but NOT if rejected.
          <ListingActionButton 
            label="Riaktivizo"
            onClick={() => onReactivate(listingId, isArchived, originalId || listingId)}
            icon={RefreshCw}
            variant="secondary"
            className="bg-green-500 text-white hover:bg-green-600"
          />
        )}
        
        {onDelete && ( // Delete button should be available based on isArchived and approvalStatus
          <ListingActionButton 
            label="Fshij"
            onClick={() => onDelete(listingId, isArchived)}
            icon={Trash2}
            variant="destructive"
            // Adjust col-span logic if necessary based on other buttons visibility
            className={`${(!isPremium && isActive && !isPending && !isRejected && !onDeactivate) || (!isActive && !isRejected && !onReactivate) ? "col-span-2" : ""}`}
          />
        )}
      </div>
    </div>
  );
};

export default ListingActionsOverlay;
